import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Handshake, CheckCircle2, XCircle, FileText, Download } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const baixarPdf = async (aid, doc, nome) => {
  try {
    const r = await api.get(`/condicoes-aceite/${aid}/documento.pdf`, { params: { doc }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = nome; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Não foi possível baixar o PDF"); }
};

export const CondicoesRede = ({ endpoint }) => {
  const [lista, setLista] = useState([]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [docView, setDocView] = useState(null); // { aid, titulo, texto, doc }

  const load = () => api.get(endpoint).then(r => setLista(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const abrirDoc = async (a, tipo) => {
    try {
      const r = await api.get(`/condicoes-aceite/${a.id}/documentos`);
      if (tipo === "aditivo") {
        setDocView({ aid: a.id, doc: "aditivo", titulo: "Termo Aditivo Comercial e Operacional", texto: r.data.aditivo });
      } else {
        setDocView({ aid: a.id, doc: "contrato", titulo: "Contrato de Prestação de Serviços (contrato-base)", texto: r.data.contrato_base });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const responder = async (a, acao) => {
    setBusy(true);
    try {
      await api.post(`/condicoes-aceite/${a.id}/responder`, { acao, confirmacoes: checks[a.id] || {} });
      toast.success(acao === "aceitar" ? "Condições aceitas — você entrou na rede desta empresa" : "Registrado: sem interesse");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggle = (aid, k) => setChecks(c => ({ ...c, [aid]: { ...(c[aid] || {}), [k]: !(c[aid] || {})[k] } }));

  return (
    <div className="p-8 max-w-4xl" data-testid="condicoes-rede-page">
      <div className="label-cap">Comercial</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1 flex items-center gap-2">
        <Handshake size={26} className="text-[#FF6700]" /> Condições das Empresas
      </h1>
      <p className="text-sm text-muted-foreground mt-2">
        Empresas da sua região publicam as condições (valores, nota fiscal, pagamento e contratos).
        Você decide se aceita trabalhar com cada uma. O aceite comercial <strong>não</strong> é o aceite de uma OS específica.
      </p>

      {lista.length === 0 && (
        <div className="border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground mt-6"
          data-testid="condicoes-vazio">Nenhuma condição publicada para a sua região no momento.</div>
      )}

      <div className="space-y-4 mt-6">
        {lista.map(a => {
          const c = a.condicoes || {};
          const fiscal = c.fiscal || {};
          const cal = c.calendario || {};
          const ck = checks[a.id] || {};
          const temContratoProprio = !!c.contrato;
          return (
            <div key={a.id} className="border border-border bg-white p-5" data-testid={`condicao-${a.id}`}>
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <div className="font-bold text-lg">{a.empresa_nome}</div>
                  <div className="text-xs text-muted-foreground">Condições v{a.versao_condicoes} · recebidas em {new Date(a.criado_em).toLocaleDateString("pt-BR")}</div>
                </div>
                {a.status === "aceito" && <span className="px-2 py-1 text-[11px] font-bold rounded bg-emerald-100 text-emerald-700 border border-emerald-300"><CheckCircle2 size={12} className="inline" /> ACEITO</span>}
                {a.status === "recusado" && <span className="px-2 py-1 text-[11px] font-bold rounded bg-red-100 text-red-700 border border-red-300"><XCircle size={12} className="inline" /> SEM INTERESSE</span>}
                {a.status === "pendente" && <span className="px-2 py-1 text-[11px] font-bold rounded bg-amber-100 text-amber-700 border border-amber-300">AGUARDANDO SUA RESPOSTA</span>}
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {(c.tabela?.itens || []).map(it => (
                  <div key={it.tipo_servico} className="flex justify-between border-b border-dashed border-border py-1">
                    <span className="capitalize">{it.tipo_servico.replace(/_/g, " ")}</span>
                    <strong>{it.modo === "percentual" ? `${it.valor_montador}%` : `R$ ${Number(it.valor_montador).toFixed(2)}`}</strong>
                  </div>
                ))}
                <div className="flex justify-between py-1"><span>Nota fiscal</span><strong>{fiscal.exige_nota_fiscal ? "EXIGIDA (CNPJ ativo)" : "Não exigida"}</strong></div>
                <div className="flex justify-between py-1"><span>Fechamento</span><strong>{cal.periodicidade || "—"}</strong></div>
              </div>
              {fiscal.exige_nota_fiscal && (
                <div className="mt-2 text-[11px] bg-red-50 border border-red-200 text-red-700 p-2 font-semibold">
                  ⚠️ Esta empresa só paga mediante NOTA FISCAL — envio em até 2 dias após cada fechamento.
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {temContratoProprio ? (
                  <a href={`${BASE}/api/condicoes/contrato/${c.contrato.id}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs underline text-[#142766] font-semibold"
                    data-testid={`condicao-contrato-${a.id}`}>
                    <FileText size={13} /> Ler contrato próprio da empresa: {c.contrato.nome} (v{c.contrato.versao})
                  </a>
                ) : (
                  <button onClick={() => abrirDoc(a, "contrato")}
                    className="inline-flex items-center gap-1 text-xs underline text-[#142766] font-semibold"
                    data-testid={`ler-contrato-base-${a.id}`}>
                    <FileText size={13} /> Ler Contrato de Prestação de Serviços (contrato-base)
                  </button>
                )}
                <button onClick={() => abrirDoc(a, "aditivo")}
                  className="inline-flex items-center gap-1 text-xs underline text-[#FF6700] font-semibold"
                  data-testid={`ler-aditivo-${a.id}`}>
                  <FileText size={13} /> Ler Termo Aditivo Comercial (condições desta versão)
                </button>
              </div>

              {a.status === "aceito" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="btn-sharp text-xs"
                    onClick={() => baixarPdf(a.id, "aditivo", `termo_aditivo_${a.empresa_nome}.pdf`)}
                    data-testid={`baixar-aditivo-${a.id}`}>
                    <Download size={13} /> Baixar Termo Aditivo (PDF)
                  </Button>
                  {!temContratoProprio && (
                    <Button size="sm" variant="outline" className="btn-sharp text-xs"
                      onClick={() => baixarPdf(a.id, "contrato", `contrato_${a.empresa_nome}.pdf`)}
                      data-testid={`baixar-contrato-${a.id}`}>
                      <Download size={13} /> Baixar Contrato (PDF)
                    </Button>
                  )}
                </div>
              )}

              {a.status === "pendente" && (
                <div className="mt-4 border-t border-border pt-3">
                  <div className="space-y-1 text-xs">
                    {[["li_tabela", "Li a Tabela de Valores"], ["li_fiscal", "Li as condições fiscais"],
                      ["li_calendario", "Li o calendário de fechamento e pagamento"],
                      temContratoProprio
                        ? ["li_contrato_empresa", "Li o contrato próprio da empresa"]
                        : ["li_contrato_base", "Li o Contrato de Prestação de Serviços (contrato-base)"],
                      ["li_aditivo", "Li o Termo Aditivo Comercial e Operacional"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!ck[k]} onChange={() => toggle(a.id, k)}
                          data-testid={`check-${k}-${a.id}`} /> {l}
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <Button onClick={() => responder(a, "aceitar")} disabled={busy}
                      className="btn-sharp flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`aceitar-condicoes-${a.id}`}>
                      <CheckCircle2 size={15} /> ACEITAR CONDIÇÕES
                    </Button>
                    <Button onClick={() => responder(a, "recusar")} disabled={busy} variant="outline"
                      className="btn-sharp flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`recusar-condicoes-${a.id}`}>
                      <XCircle size={15} /> NÃO TENHO INTERESSE
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!docView} onOpenChange={o => !o && setDocView(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{docView?.titulo}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 border border-border bg-[#FAFAFA] p-4">
            <pre className="whitespace-pre-wrap text-xs font-sans leading-relaxed" data-testid="doc-texto">{docView?.texto}</pre>
          </div>
          <Button variant="outline" className="btn-sharp"
            onClick={() => baixarPdf(docView.aid, docView.doc, `${docView.doc}.pdf`)}
            data-testid="doc-baixar-pdf">
            <Download size={14} /> Baixar PDF
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CondicoesRede;
